import { oc } from '@orpc/contract';
import { OperationEngineTargetSchema } from '@standandforce/api-contract';
import { z } from 'zod';

/**
 * Labor Core 年末調整 内容確認（タグ別インスタンス一覧）。
 * Backend: list-year-end-adjustment-instances-by-tag.api-contract.ts
 */

const yearEndAdjustmentDocumentStatusValues = ['notApplicable', 'notSubmitted', 'submitted', 'returned', 'confirmed'] as const;
const yearEndAdjustmentOverallStatusValues = ['completed', 'returned', 'awaitingReview', 'pendingAction'] as const;
const listOrderFields = z.enum(['target', 'overallStatus', 'dueDate', 'lastUpdatedAt']);
const documentFocusFields = z.enum(['dependentDeduction', 'spouseDeduction', 'insurancePremium', 'housingLoan', 'previousEmployer']);
const YearEndAdjustmentDocumentStatusSchema = z.enum(yearEndAdjustmentDocumentStatusValues);
const YearEndAdjustmentOverallStatusSchema = z.enum(yearEndAdjustmentOverallStatusValues);
const ListYearEndAdjustmentInstancesByTagRowSchema = z.object({
  operationPackageInstanceId: z.string(),
  target: OperationEngineTargetSchema,
  overallStatus: YearEndAdjustmentOverallStatusSchema,
  dependentDeduction: YearEndAdjustmentDocumentStatusSchema,
  spouseDeduction: YearEndAdjustmentDocumentStatusSchema,
  insurancePremium: YearEndAdjustmentDocumentStatusSchema,
  housingLoan: YearEndAdjustmentDocumentStatusSchema,
  previousEmployer: YearEndAdjustmentDocumentStatusSchema,
  dueDate: z.string().nullable(),
  lastUpdatedAt: z.string().nullable(),
  /** 別途対応としてマークされた書類（BE 未接続時は mock のみ） */
  separateHandlingDocuments: z.array(documentFocusFields).optional()
});
const ListYearEndAdjustmentInstancesByTagInputSchema = z.object({
  tagId: z.string(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(2000).default(50),
  orderBy: listOrderFields.optional(),
  orderDirection: z.enum(['asc', 'desc']).default('asc'),
  searchText: z.string().max(500).optional(),
  status: z.array(YearEndAdjustmentOverallStatusSchema).optional(),
  documentFocus: documentFocusFields.optional()
});
const ListYearEndAdjustmentInstancesByTagOutputSchema = z.object({
  items: z.array(ListYearEndAdjustmentInstancesByTagRowSchema),
  length: z.number(),
  count: z.number(),
  page: z.number(),
  maxPage: z.number()
});
const listYearEndAdjustmentInstancesByTagContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/HRF_LABOR_CORE/instance-tags/{tagId}/year-end-adjustment-instances'
}).input(ListYearEndAdjustmentInstancesByTagInputSchema).output(ListYearEndAdjustmentInstancesByTagOutputSchema);
export type YearEndAdjustmentDocumentStatus = z.infer<typeof YearEndAdjustmentDocumentStatusSchema>;
export type YearEndAdjustmentOverallStatus = z.infer<typeof YearEndAdjustmentOverallStatusSchema>;
export type ListYearEndAdjustmentInstancesByTagInput = z.infer<typeof ListYearEndAdjustmentInstancesByTagInputSchema>;
export type ListYearEndAdjustmentInstancesByTagOutput = z.infer<typeof ListYearEndAdjustmentInstancesByTagOutputSchema>;
export type ListYearEndAdjustmentInstancesByTagRowOutput = z.infer<typeof ListYearEndAdjustmentInstancesByTagRowSchema>;
const CountYearEndAdjustmentInstancesByTagDocumentTabsInputSchema = z.object({
  tagId: z.string(),
  searchText: z.string().max(500).optional(),
  status: z.array(YearEndAdjustmentOverallStatusSchema).optional()
});
const CountYearEndAdjustmentInstancesByTagDocumentTabsOutputSchema = z.object({
  all: z.number(),
  dependentDeduction: z.number(),
  spouseDeduction: z.number(),
  insurancePremium: z.number(),
  housingLoan: z.number(),
  previousEmployer: z.number()
});
const countYearEndAdjustmentInstancesByTagDocumentTabsContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/HRF_LABOR_CORE/instance-tags/{tagId}/year-end-adjustment-instances/document-tab-counts'
}).input(CountYearEndAdjustmentInstancesByTagDocumentTabsInputSchema).output(CountYearEndAdjustmentInstancesByTagDocumentTabsOutputSchema);
export type CountYearEndAdjustmentInstancesByTagDocumentTabsInput = z.infer<typeof CountYearEndAdjustmentInstancesByTagDocumentTabsInputSchema>;
export type CountYearEndAdjustmentInstancesByTagDocumentTabsOutput = z.infer<typeof CountYearEndAdjustmentInstancesByTagDocumentTabsOutputSchema>;
export const laborCoreYearEndAdjustmentInstancesContract = oc.router({
  listByTag: listYearEndAdjustmentInstancesByTagContract,
  documentTabCounts: countYearEndAdjustmentInstancesByTagDocumentTabsContract
});